import { useForm } from 'react-hook-form'
import { Box, DialogContent, DialogActions, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import InputWithValidation from '../../shared/components/InputWithValidation'
import CustomSelect from '../../shared/components/CustomSelect'
import SharedButton from '../../shared/components/Button'
import SharedDialog from '../../shared/components/Dialog'
import PageHeader from '../../shared/components/PageHeader'
import ActionIconButton from '../../shared/components/ActionIconButton'
import StatusChip from '../../shared/components/StatusChip'
import SharedTable from '../../shared/components/Table'
import { useThemeMode, useTablePagination } from '../../shared/hooks'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined'
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { notify } from '../../shared/utilities/util'
import { getAdminAccountStatusConfig, getAdminRoleConfig } from '../../shared/utilities/statusConfig'
import { getDialogTextFieldSx } from '../../shared/styles/formStyles'
import { useAppSelector } from '../../store/hooks'
import type { AdminRole, I_AdminRow } from '../../logic/interfaces'
import type { BaseQueryErrorShape } from '../../store/api/baseQuery'
import {
  useGetAdminsQuery,
  useCreateAdminMutation,
  useResendAdminInviteMutation,
  useDeleteAdminMutation,
  usePermDeleteAdminMutation,
  useRestoreAdminMutation,
} from '../../store/api/adminApi'

const columns = [
  { id: 'name', label: 'Name', minWidth: 170 },
  { id: 'email', label: 'Email', minWidth: 200 },
  { id: 'role', label: 'Role', minWidth: 130 },
  { id: 'status', label: 'Status', minWidth: 120 },
  { id: 'createdAt', label: 'Created', minWidth: 130 },
  { id: 'action', label: 'Action', minWidth: 120 },
]

const SUPER_ADMIN: AdminRole = 'superadmin'

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: SUPER_ADMIN, label: 'Super Admin' },
]

type AccountStatus = 'active' | 'invited' | 'deleted'

/**
 * `deleted` always wins. `status` is what the invite flow writes; `invitePending`
 * is the defensive fallback for rows created before it shipped, which are plain
 * Active accounts.
 */
const getAccountStatus = (row: I_AdminRow): AccountStatus => {
  if (row?.deleted) return 'deleted'
  if (row?.status === 'invited' || row?.invitePending === true) return 'invited'
  return 'active'
}

const formatCreatedAt = (value?: string) =>
  value ? new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '--'

const EMPTY_ADMINS: I_AdminRow[] = []

interface AddAdminFormValues {
  name: string
  email: string
  role: AdminRole
}

const Admin = () => {
  const { isDark } = useThemeMode()
  const { data: adminList, isLoading: adminLoader } = useGetAdminsQuery()
  // Stable identity while the query is still loading, so the memos below don't
  // recompute on a fresh `[]` every render.
  const allAdmin = useMemo(() => adminList?.admins ?? EMPTY_ADMINS, [adminList])
  // Server-reported row count. Listing is unpaginated, so it equals
  // `allAdmin.length` — it is read rather than recomputed so the footer stays
  // correct if the endpoint ever starts paging.
  const serverTotal = adminList?.total
  const [createAdmin] = useCreateAdminMutation()
  const [resendInviteMut] = useResendAdminInviteMutation()
  const [deleteAdminMut] = useDeleteAdminMutation()
  const [permDeleteAdminMut] = usePermDeleteAdminMutation()
  const [restoreAdminMut] = useRestoreAdminMutation()
  const {
    page,
    rowsPerPage,
    totalCount,
    setTotalCount,
    searchValue,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
  } = useTablePagination({ key: 'admins' })

  const currentAdminId = useAppSelector((state) => state.auth.user.id)
  const currentAdminRole = useAppSelector((state) => state.auth.user.role)
  // The Role select is SuperAdmin-only. Presentation only — the real gate is
  // server-side on POST /admin/add-admin, which rejects a non-SuperAdmin and
  // forces the role it stores. The route is also guarded client-side now
  // (`adminPath` is wrapped in `RequireSuperAdmin` in router.tsx), and the
  // sidebar link is hidden — but neither is the gate, only the UX.
  const canAssignRole = currentAdminRole === SUPER_ADMIN

  const [open, setOpen] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [perm, setPerm] = useState(false)
  const [adminId, setAdminId] = useState<string | null>(null)
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null)
  // The account exists but the invite email did not go out (`inviteSent: false`).
  // Kept separate from the success state so the copy can point at Resend invite.
  const [inviteFailed, setInviteFailed] = useState<{ email: string; message: string } | null>(null)
  const [duplicateEmail, setDuplicateEmail] = useState<string | null>(null)
  const [addAdminLoader, setAddAdminLoader] = useState(false)
  const [deleteLoader, setDeleteLoader] = useState(false)
  // A refusal the server returned for THIS delete. Rendered inside the confirm
  // dialog rather than as a toast — see `handleDelete`.
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddAdminFormValues>({
    defaultValues: { name: '', email: '', role: 'admin' },
  })

  // Removing the last active Super Admin would lock the whole platform out:
  // no code path can create one except this dialog, so there would be nobody
  // left able to invite a replacement.
  //
  // ACTIVE only — a never-accepted SuperAdmin invite is NOT a survivor, which is
  // the same line the backend draws (it counts remaining SuperAdmins that hold a
  // password and 409s otherwise). Counting `!deleted` alone left the Delete
  // button enabled and its explanatory tooltip hidden whenever the second
  // SuperAdmin was still pending, so the operator only learned the rule from a
  // server error toast. `getAccountStatus` reads the status the backend derives
  // and sends on the row; nothing is re-derived here.
  //
  // The ids, not just the count: the rule is about who would be LEFT, so the
  // row being deleted has to come out of the population first — exactly what
  // `countSignInCapableSuperAdmins(target._id)` does server-side. Counting the
  // whole active population instead disabled Delete on a SuperAdmin row that is
  // itself only *invited* (survivors = 1, so `<= 1` blocked it) even though the
  // server would have allowed it — leaving a SuperAdmin invite to a mistyped
  // address impossible to cancel from the UI.
  //
  // `isDisabled` is the third clause, and it is the server's own definition of
  // a survivor, not an extra client rule: `countSignInCapableSuperAdmins`
  // filters `isDisabled: { $ne: true }` because `checkSubjectActive` refuses a
  // disabled admin at every token issuance and refresh — such a row genuinely
  // cannot sign in. Counting it here would leave Delete enabled (and its
  // tooltip hidden) on the last SuperAdmin who actually can, and the operator
  // would meet the rule as a 409 instead of a disabled button. `!== true`, not
  // `=== false`: the flag is absent on almost every row, and absent means
  // enabled. Status alone does not cover it — `deriveAdminStatus` reports a
  // disabled row with a password as `active`.
  const activeSuperAdminIds = useMemo(
    () =>
      allAdmin
        .filter((row) => row?.role === SUPER_ADMIN && getAccountStatus(row) === 'active' && row?.isDisabled !== true)
        .map((row) => row._id),
    [allAdmin]
  )

  // Survivors if `rowId` were removed. Mirrors the server refusal, which fires
  // when this reaches 0.
  const superAdminsAfterRemoving = (rowId: string) => activeSuperAdminIds.filter((id) => id !== rowId).length

  const filteredAdmins = useMemo(() => {
    const term = searchValue.trim().toLowerCase()
    if (!term) return allAdmin
    return allAdmin.filter((row) => {
      const status = getAccountStatus(row)
      return [
        row?.name,
        row?.email,
        getAdminRoleConfig(row?.role).label,
        getAdminAccountStatusConfig(status).label,
      ].some((value) => (value || '').toLowerCase().includes(term))
    })
  }, [allAdmin, searchValue])

  // The footer used to be hard-wired to a totalCount nothing ever wrote, so it
  // always read "0-0 of 0". With no search term the server's `total` is what
  // the footer reports; a search narrows the set client-side (the list is
  // returned unpaginated), so the footer must then describe the filtered set
  // or the row range would exceed the total.
  const isFiltered = searchValue.trim().length > 0
  useEffect(() => {
    setTotalCount(isFiltered ? filteredAdmins.length : serverTotal ?? filteredAdmins.length)
  }, [isFiltered, filteredAdmins.length, serverTotal, setTotalCount])

  // A search (or a delete) can shrink the list past the current page.
  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredAdmins.length) {
      handlePageChange(0)
    }
  }, [filteredAdmins.length, page, rowsPerPage, handlePageChange])

  const pagedAdmins = useMemo(
    () => filteredAdmins.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredAdmins, page, rowsPerPage]
  )

  /**
   * The dialog STAYS OPEN on failure and renders the server's message in place.
   *
   * Every refusal these routes return is an instruction, not a status ping: the
   * last-Super-Admin rule, and the 409 for a row that still holds the platform
   * API key, which tells the operator to generate a new key from Settings
   * first. Closing the dialog and firing a toast (what this did) put that
   * sentence behind a fading notification at the exact moment it was needed,
   * and left the operator on a list with no indication of what to do next.
   * Both mutations are SILENT so the interceptor does not report it twice.
   *
   * `setOpenDelete(false)` also used to run unconditionally after the
   * try/catch, so the dialog closed even on a refusal.
   */
  const handleDelete = async () => {
    setDeleteLoader(true)
    setDeleteError(null)
    try {
      const result = perm ? await permDeleteAdminMut(adminId).unwrap() : await deleteAdminMut(adminId).unwrap()
      setOpenDelete(false)
      setPerm(false)
      notify(result?.message || 'Admin removed', 'success')
    } catch (error) {
      const failure = error as BaseQueryErrorShape
      setDeleteError(failure?.message || 'We could not remove this admin. Please try again.')
    } finally {
      setDeleteLoader(false)
    }
  }

  const handleRestore = async (id: string) => {
    try {
      const result = await restoreAdminMut(id).unwrap()
      if (result) {
        notify(result?.message || 'Admin restored', 'success')
      }
    } catch (error) {
      console.error('Error restoring data:', error)
    }
  }

  const handleResendInvite = async (row: I_AdminRow) => {
    setResendingId(row._id)
    try {
      const result = await resendInviteMut(row._id).unwrap()
      // The route answers 2xx for "link regenerated but the mail failed" too.
      // Strict `=== false` so a response without the flag keeps the success
      // path, exactly as the Add Admin dialog treats createAdmin. Rendering
      // that case green told the operator the invitee had a link when the old
      // one had just been invalidated and no new one was delivered.
      if (result?.inviteSent === false) {
        notify(
          result?.message ||
            `The invite link for ${row.email} was regenerated, but the email could not be sent. Try Resend invite again.`,
          'warning'
        )
        return
      }
      notify(result?.message || `Invite resent to ${row.email}`, 'success')
    } catch (error) {
      console.error('Error resending invite:', error)
    } finally {
      setResendingId(null)
    }
  }

  const onSubmit = async (data: AddAdminFormValues) => {
    const email = data.email.trim()
    setAddAdminLoader(true)
    try {
      const result = await createAdmin({
        name: data.name.trim(),
        email,
        role: canAssignRole ? data.role : 'admin',
      }).unwrap()
      setOpen(false)
      // The route answers 200 for "created but the mail failed" too. Strict
      // `=== false` so a response without the flag keeps the success path.
      if (result?.inviteSent === false) {
        setInviteFailed({
          email,
          message: result?.message || 'Admin created, but the invite email could not be sent. Use Resend invite.',
        })
        return
      }
      setInviteSentTo(email)
    } catch (error) {
      const failure = error as BaseQueryErrorShape
      // The duplicate-email case gets its own alert (it used to be a 500 with
      // a raw Mongo error behind a generic toast).
      if (failure?.status === 409) {
        setOpen(false)
        setDuplicateEmail(email)
        return
      }
      notify(failure?.message || 'We could not send the invite. Please try again.', 'error')
    } finally {
      setAddAdminLoader(false)
    }
  }

  const handleDeleteClick = (row: I_AdminRow, isPerm?: boolean) => {
    setAdminId(row._id)
    setPerm(!!isPerm)
    // A refusal belongs to the row it was raised for — never carry it into the
    // next dialog.
    setDeleteError(null)
    setOpenDelete(true)
  }

  const handleOpen = () => {
    setOpen(true)
    reset({ name: '', email: '', role: 'admin' })
  }

  const handleClose = () => {
    setOpen(false)
    setOpenDelete(false)
    setDeleteError(null)
  }

  const renderActions = (row: I_AdminRow, status: AccountStatus) => {
    if (status === 'deleted') {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <ActionIconButton
            tooltip="Restore"
            icon={<RestoreFromTrashOutlinedIcon fontSize="small" />}
            onClick={() => handleRestore(row._id)}
            variant="restore"
          />
          <ActionIconButton
            tooltip="Delete permanently"
            icon={<DeleteForeverOutlinedIcon fontSize="small" />}
            onClick={() => handleDeleteClick(row, true)}
            variant="danger"
          />
        </Box>
      )
    }

    const isSelf = !!currentAdminId && row._id === currentAdminId
    const isLastSuperAdmin = row?.role === SUPER_ADMIN && superAdminsAfterRemoving(row._id) === 0
    // The last-SuperAdmin wording mirrors the server refusal in meaning: a
    // SuperAdmin invite that has not been accepted yet is not a survivor, so
    // "invite another one" on its own would still leave the operator blocked.
    const deleteBlockedReason = isSelf
      ? "You can't remove your own account. Ask another Super Admin."
      : isLastSuperAdmin
      ? 'This is the last active Super Admin. Invite another Super Admin and wait for them to accept before removing this account.'
      : ''

    return (
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
        {status === 'invited' && (
          <ActionIconButton
            tooltip={resendingId === row._id ? 'Sending…' : 'Resend invite'}
            icon={<ForwardToInboxOutlinedIcon fontSize="small" />}
            onClick={() => handleResendInvite(row)}
            variant="edit"
            disabled={resendingId === row._id}
          />
        )}
        <ActionIconButton
          tooltip={deleteBlockedReason || 'Delete'}
          icon={<DeleteOutlinedIcon fontSize="small" />}
          onClick={() => handleDeleteClick(row)}
          variant="danger"
          disabled={!!deleteBlockedReason}
        />
      </Box>
    )
  }

  const formattedData = pagedAdmins.map((row) => {
    const status = getAccountStatus(row)
    const roleChip = getAdminRoleConfig(row?.role)
    const statusChip = getAdminAccountStatusConfig(status)
    const isSelf = !!currentAdminId && row._id === currentAdminId
    const struckThrough = status === 'deleted' ? { textDecoration: 'line-through', color: 'text.disabled' } : {}

    return {
      id: row._id,
      name: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 600, ...struckThrough }}>
            {row?.name || '--'}
          </Typography>
          {isSelf && (
            <StatusChip
              label="You"
              color="#475569"
              bg="#f1f5f9"
              darkColor="#94a3b8"
              darkBg="rgba(148, 163, 184, 0.12)"
            />
          )}
        </Box>
      ),
      email: (
        <Typography component="span" sx={{ fontSize: 'inherit', color: 'text.secondary', ...struckThrough }}>
          {row?.email || '--'}
        </Typography>
      ),
      role: (
        <StatusChip
          label={roleChip.label}
          color={roleChip.color}
          bg={roleChip.bg}
          darkColor={roleChip.darkColor}
          darkBg={roleChip.darkBg}
        />
      ),
      status: (
        <StatusChip
          label={statusChip.label}
          color={statusChip.color}
          bg={statusChip.bg}
          darkColor={statusChip.darkColor}
          darkBg={statusChip.darkBg}
        />
      ),
      createdAt: formatCreatedAt(row?.createdAt),
      deleted: row?.deleted,
      action: renderActions(row, status),
    }
  })

  const dialogTextFieldSx = getDialogTextFieldSx(isDark)

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Admin Users"
        subtitle="Manage administrator accounts and permissions"
        actions={
          <SharedButton label="Add Admin" onClick={handleOpen} startIcon={<AddOutlinedIcon fontSize="small" />} />
        }
      />

      <SharedTable
        columns={columns}
        data={formattedData}
        page={page}
        tableBodyLoader={adminLoader}
        rowsPerPage={rowsPerPage}
        totalRows={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        searchFilter={undefined}
        searchFilterVisible={true}
        onSearch={handleSearch}
        initialSearchValue={searchValue}
        selectableRows={false}
        onRowClick={undefined}
        getRowHref={undefined}
        showDeletedToggle={false}
        switchProps={undefined}
        // Name/Role/Status cells are React nodes, which the table's internal
        // string filter cannot read — search and paging are done above.
        disableClientFilter={true}
      />

      <SharedDialog
        variant="form"
        open={open}
        onClose={handleClose}
        title="Add Admin"
        subtitle="They'll get an email invite to set their own password"
        actions={null}
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent sx={{ pt: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <InputWithValidation
              name="name"
              control={control}
              errors={errors}
              rules={{
                required: 'Full name is required',
                validate: (value: string) => value.trim().length > 0 || 'Full name is required',
              }}
              label="Full Name *"
              placeholder="e.g. Jordan Blake"
              sx={dialogTextFieldSx}
            />
            <Box>
              <InputWithValidation
                name="email"
                control={control}
                errors={errors}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                }}
                label="Email *"
                type="email"
                placeholder="name@company.com"
                sx={dialogTextFieldSx}
              />
              {/* InputWithValidation owns `helperText` for the error message,
                  so the hint sits underneath and yields to an error. */}
              {!errors.email && (
                <Typography
                  component="div"
                  sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.5, mt: 0.75, ml: 0.25 }}
                >
                  The invite link is sent here and expires in 72 hours.
                </Typography>
              )}
            </Box>
            {canAssignRole && (
              <Box>
                <CustomSelect
                  name="role"
                  control={control}
                  options={roleOptions}
                  label="Role *"
                  sx={dialogTextFieldSx}
                />
                {/*
                  Nine routes are SuperAdmin-only and only six of them are admin management —
                  generate-api-key and force-update-mobile-app are platform controls, so
                  "plus adding and removing admins" understated what the role hands over.
                */}
                <Typography
                  component="div"
                  sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.5, mt: 0.75, ml: 0.25 }}
                >
                  <strong>Admin</strong> — subscribers, plans, support, AI spend.
                  <br />
                  <strong>Super Admin</strong> — all of that, plus managing admin accounts and platform-level settings
                  (API key, mobile force-update).
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1.5 }}>
            <SharedButton label="Cancel" btnVariant="secondary" fullWidth onClick={handleClose} />
            <SharedButton
              label="Send Invite"
              fullWidth
              type="submit"
              loading={addAdminLoader}
              loadingText="Sending..."
            />
          </DialogActions>
        </Box>
      </SharedDialog>

      <SharedDialog
        variant="alert"
        open={!!inviteSentTo}
        onClose={() => setInviteSentTo(null)}
        icon={<CheckCircleOutlinedIcon sx={{ fontSize: 28, color: '#16a34a' }} />}
        iconBg="#f0fdf4"
        title="Invite sent"
        description={`${
          inviteSentTo ?? ''
        } has 72 hours to set their password. They won't appear as Active until they do.`}
        buttonLabel="Done"
      />

      <SharedDialog
        variant="alert"
        open={!!inviteFailed}
        onClose={() => setInviteFailed(null)}
        icon={<WarningAmberOutlinedIcon sx={{ fontSize: 28, color: '#f59e0b' }} />}
        iconBg="#fffbeb"
        title="Account created — invite email failed"
        description={`${inviteFailed?.message ?? ''} ${
          inviteFailed?.email ?? ''
        } is in the list below as Invited; use the Resend invite action on that row to try again.`}
        buttonLabel="Got it"
      />

      <SharedDialog
        variant="alert"
        open={!!duplicateEmail}
        onClose={() => setDuplicateEmail(null)}
        icon={<ErrorOutlineIcon sx={{ fontSize: 28, color: '#ef4444' }} />}
        iconBg="#fef2f2"
        title="That email already has an account"
        description={`${duplicateEmail ?? ''} is already an admin. Resend their invite from the list instead.`}
        buttonLabel="Got it"
      />

      <SharedDialog
        variant="confirm"
        open={openDelete}
        onClose={handleClose}
        icon={<DeleteOutlinedIcon sx={{ fontSize: 28, color: '#ef4444' }} />}
        iconBg="#fef2f2"
        title={`${perm ? 'Permanently Delete' : 'Delete'} Admin`}
        description={`Are you sure you want to ${perm ? 'permanently ' : ''}delete this admin? ${
          perm ? 'This action cannot be undone.' : ''
        }`}
        confirmLabel={perm ? 'Delete Forever' : 'Delete'}
        confirmColor="error"
        onConfirm={handleDelete}
        confirmLoading={deleteLoader}
        error={deleteError ?? undefined}
      />
    </Box>
  )
}

export default Admin
